import { PLATFORM_BASE_URL } from "../platform/config";
import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React from "react";
import {
  OktonDataGrid,
  OktonDataGridColumn,
  OktonInfoCard,
  OktonMetricCard,
  OktonPageHeader,
  useOktonAuth,
  OktonErrorState,
  OktonInput,
  OktonSelect,
  formatDate,
  formatDateTime,
} from "@okton/platform-ui";

interface LicenseRow {
  code: number;
  active: boolean;
  status: "active" | "expired" | "inactive";
  companyCode: number;
  branchCode: number;
  branchName?: string;
  legalName?: string;
  document?: string;
  machine?: string;
  expiresOn?: string;
  moduleCount: number;
  deviceCount: number;
  syncedAt?: string;
  note?: string;
  version?: string;
}

interface LicenseDevice {
  code: string | number;
  module_code?: string | number;
  module_name?: string;
  machine?: string;
  version?: string;
  synced_at?: string;
  note?: string;
}

interface LicenseHistory {
  code: string | number;
  created_at?: string;
  event_type?: string;
  module_code?: string | number;
  module_name?: string;
  previous_active?: boolean | number | string | null;
  active?: boolean | number | string | null;
  previous_expiry?: string;
  expiry?: string;
  previous_machine?: string;
  machine?: string;
  justification?: string;
}

interface LicenseDetail {
  license: LicenseRow;
  devices: LicenseDevice[];
  history: LicenseHistory[];
}

function statusLabel(status: LicenseRow["status"]): string {
  if (status === "active") return "Ativa";
  if (status === "expired") return "Vencida";
  return "Inativa";
}

function statusColor(status: LicenseRow["status"]): string {
  if (status === "active") return "green";
  if (status === "expired") return "red";
  return "gray";
}

export const LicensesPage: React.FC = () => {
  const { accessToken } = useOktonAuth();
  const [rows, setRows] = React.useState<LicenseRow[]>([]);
  const [detail, setDetail] = React.useState<LicenseDetail | null>(null);
  const [summary, setSummary] = React.useState({
    total: 0,
    active: 0,
    expired: 0,
    inactive: 0,
    devices: 0,
  });
  const [query, setQuery] = React.useState("");
  const [status, setStatus] = React.useState("all");
  const [page, setPage] = React.useState(1);
  const [pages, setPages] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const modal = useDisclosure();

  const load = React.useCallback(
    async (targetPage = page, targetQuery = query, targetStatus = status) => {
      if (!accessToken) return;
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          page: String(targetPage),
          limit: "50",
          status: targetStatus,
        });
        if (targetQuery.trim()) params.set("q", targetQuery.trim());

        const response = await fetch(
          `${PLATFORM_BASE_URL}/v1/admin/licenses?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: "include",
          },
        );
        const payload = await response.json();
        if (!response.ok)
          throw new Error(
            payload.message || "Não foi possível carregar as licenças.",
          );

        const data = payload.data || {};
        setRows(data.rows || []);
        setSummary(data.summary || {});
        setPage(Number(data.page || 1));
        setPages(Number(data.pages || 1));
        setTotal(Number(data.total || 0));
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Erro ao carregar licenças.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken, page, query, status],
  );

  React.useEffect(() => {
    load();
  }, [accessToken]);

  async function openLicense(code: number): Promise<void> {
    if (!accessToken) return;
    setError("");
    const response = await fetch(
      `${PLATFORM_BASE_URL}/v1/admin/licenses/${code}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: "include",
      },
    );
    const payload = await response.json();
    if (!response.ok) {
      setError(payload.message || "Não foi possível carregar a licença.");
      return;
    }
    setDetail(payload.data);
    modal.onOpen();
  }

  const columns = React.useMemo<OktonDataGridColumn<LicenseRow>[]>(
    () => [
      {
        key: "license",
        header: "Licença",
        render: (row) => <Text fontWeight="700">{row.code}</Text>,
      },
      {
        key: "branch",
        header: "Empresa / Filial",
        render: (row) => (
          <Box>
            <Text fontWeight="600">
              {row.companyCode}.{row.branchCode} -{" "}
              {row.branchName || row.legalName || "Filial não encontrada"}
            </Text>
            {row.legalName ? (
              <Text fontSize="xs" color="gray.500">
                {row.legalName}
              </Text>
            ) : null}
          </Box>
        ),
      },
      {
        key: "document",
        header: "Documento",
        value: (row) => row.document || "-",
      },
      {
        key: "expiry",
        header: "Vencimento",
        value: (row) => formatDate(row.expiresOn),
      },
      {
        key: "devices",
        header: "Dispositivos",
        value: (row) => row.deviceCount,
      },
      { key: "version", header: "Versão", value: (row) => row.version || "-" },
      {
        key: "status",
        header: "Status",
        render: (row) => (
          <Badge colorScheme={statusColor(row.status)}>
            {statusLabel(row.status)}
          </Badge>
        ),
      },
      {
        key: "actions",
        header: "",
        render: (row) => (
          <Button size="xs" onClick={() => openLicense(row.code)}>
            Abrir
          </Button>
        ),
      },
    ],
    [],
  );

  const deviceColumns: OktonDataGridColumn<LicenseDevice>[] = [
    { key: "code", header: "Código", value: (device) => device.code },
    {
      key: "module",
      header: "Módulo",
      value: (device) =>
        device.module_code
          ? `${device.module_code} - ${device.module_name || "Módulo"}`
          : device.module_name || "-",
    },
    {
      key: "machine",
      header: "Máquina",
      value: (device) => device.machine || "-",
    },
    {
      key: "version",
      header: "Versão",
      value: (device) => device.version || "-",
    },
    {
      key: "sync",
      header: "Sincronização",
      value: (device) => formatDateTime(device.synced_at),
    },
    {
      key: "note",
      header: "Observação",
      value: (device) => device.note || "-",
    },
  ];

  const historyColumns: OktonDataGridColumn<LicenseHistory>[] = [
    {
      key: "date",
      header: "Data",
      render: (item) => (
        <Text whiteSpace="nowrap">{formatDateTime(item.created_at)}</Text>
      ),
    },
    { key: "type", header: "Tipo", value: (item) => item.event_type || "-" },
    {
      key: "module",
      header: "Módulo",
      value: (item) =>
        item.module_code
          ? `${item.module_code} - ${item.module_name || "Módulo"}`
          : "-",
    },
    {
      key: "change",
      header: "Alteração",
      render: (item) => (
        <Box>
          <Text fontSize="xs">
            Ativo: {String(item.previous_active ?? "-")} →{" "}
            {String(item.active ?? "-")}
          </Text>
          {item.previous_expiry || item.expiry ? (
            <Text fontSize="xs">
              Venc.: {formatDate(item.previous_expiry)} →{" "}
              {formatDate(item.expiry)}
            </Text>
          ) : null}
          {item.previous_machine || item.machine ? (
            <Text fontSize="xs">
              Máquina: {item.previous_machine || "-"} → {item.machine || "-"}
            </Text>
          ) : null}
        </Box>
      ),
    },
    {
      key: "reason",
      header: "Justificativa",
      value: (item) => item.justification || "-",
    },
  ];

  function applyFilters(): void {
    load(1, query, status);
  }

  function clearFilters(): void {
    setQuery("");
    setStatus("all");
    load(1, "", "all");
  }

  return (
    <Box px={[4, 6, 9]} py={[6, 8, 16]} w="100%">
      <OktonPageHeader
        title="Licenças"
        subtitle="Diagnóstico central de licenças, dispositivos e histórico sincronizados do iFlex."
        actions={
          <Button variant="outline" onClick={() => load()} isLoading={loading}>
            Atualizar tela
          </Button>
        }
      />

      <SimpleGrid columns={[1, 2, 4]} spacing="4" mb="6">
        <OktonMetricCard label="Ativas" value={Number(summary.active || 0)} />
        <OktonMetricCard
          label="Vencidas"
          value={Number(summary.expired || 0)}
        />
        <OktonMetricCard
          label="Inativas"
          value={Number(summary.inactive || 0)}
        />
        <OktonMetricCard
          label="Dispositivos"
          value={Number(summary.devices || 0)}
        />
      </SimpleGrid>

      <Flex
        gap="3"
        mb="4"
        direction={["column", "row"]}
        align={["stretch", "flex-end"]}
      >
        <Box maxW="430px" w="100%">
          <OktonInput
            name="license-search"
            label="Buscar"
            value={query}
            placeholder="Licença, empresa, filial, CNPJ ou máquina"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilters();
            }}
          />
        </Box>
        <Box maxW="220px" w="100%">
          <OktonSelect
            name="license-status"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="active">Ativas</option>
            <option value="expired">Vencidas</option>
            <option value="inactive">Inativas</option>
          </OktonSelect>
        </Box>
        <Button colorScheme="blue" onClick={applyFilters}>
          Filtrar
        </Button>
        <Button variant="outline" onClick={clearFilters}>
          Limpar
        </Button>
      </Flex>

      <Flex justify="space-between" mb="3">
        <Text fontSize="sm" color="gray.500">
          {total} licença{total === 1 ? "" : "s"}
        </Text>
        <Text fontSize="sm" color="gray.500">
          Página {page} de {pages}
        </Text>
      </Flex>

      {error ? (
        <OktonErrorState
          title="Não foi possível carregar as licenças"
          description={error}
          minH="180px"
        />
      ) : (
        <OktonDataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.code}
          loading={loading}
          emptyMessage="Nenhuma licença sincronizada. Use Atualizar do iFlex na tela de Empresas para trazer Licenças, Dispositivos e Histórico."
          hidePagination
        />
      )}

      <Flex justify="space-between" mt="4">
        <Button
          variant="outline"
          isDisabled={page <= 1 || loading}
          onClick={() => load(page - 1, query, status)}
        >
          Anterior
        </Button>
        <Button
          variant="outline"
          isDisabled={page >= pages || loading}
          onClick={() => load(page + 1, query, status)}
        >
          Próxima
        </Button>
      </Flex>

      <Modal
        isOpen={modal.isOpen}
        onClose={modal.onClose}
        size="6xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {detail
              ? `Licença ${detail.license.code} - ${detail.license.branchName || "Filial"}`
              : "Licença"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {detail && (
              <>
                <SimpleGrid columns={[1, 2, 4]} spacing="4" mb="5">
                  <OktonInfoCard
                    label="Status"
                    value={statusLabel(detail.license.status)}
                  />
                  <OktonInfoCard
                    label="Vencimento"
                    value={formatDate(detail.license.expiresOn)}
                  />
                  <OktonInfoCard
                    label="Dispositivos"
                    value={String(detail.devices.length)}
                  />
                  <OktonInfoCard
                    label="Versão"
                    value={detail.license.version || "-"}
                  />
                </SimpleGrid>

                <Text fontSize="sm" color="gray.500" mb="4">
                  Empresa {detail.license.companyCode} • Filial{" "}
                  {detail.license.branchCode}
                  {detail.license.machine
                    ? ` • Máquina: ${detail.license.machine}`
                    : ""}
                </Text>

                <Tabs colorScheme="blue">
                  <TabList>
                    <Tab>Dispositivos ({detail.devices.length})</Tab>
                    <Tab>Histórico ({detail.history.length})</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel px="0">
                      <OktonDataGrid
                        rows={detail.devices}
                        columns={deviceColumns}
                        getRowId={(device) => device.code}
                        emptyMessage="Nenhum dispositivo vinculado."
                        hidePagination
                      />
                    </TabPanel>
                    <TabPanel px="0">
                      <OktonDataGrid
                        rows={detail.history}
                        columns={historyColumns}
                        getRowId={(item) => item.code}
                        emptyMessage="Nenhum histórico registrado."
                        hidePagination
                      />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
